import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { GroceryItem } from '../lib/protocolTypes'
import type { NearbyStore } from '../lib/storeTypes'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FullDisclaimerModal } from '../components/compliance/Disclaimer'
import CalendarConnectSheet from '../components/calendar/CalendarConnectSheet'
import WeeklyCalendarSheet from '../components/calendar/WeeklyCalendarSheet'
import BottomTabBar, { type AppTabId } from '../components/mobile/BottomTabBar'
import CoachTab from '../components/tabs/CoachTab'
import MealsTab from '../components/tabs/MealsTab'
import ProfileTab from '../components/tabs/ProfileTab'
import ShoppingTab from '../components/tabs/ShoppingTab'
import { useFeatureGate } from '../hooks/useFeatureGate'
import { useUserLanguage } from '../hooks/useUserLanguage'
import { dismissConflict, loadPendingConflictAlert, rescheduleConflict, type ConflictAlert, type SuggestedSlot } from '../lib/conflicts'
import { startGoogleCalendarOAuth, syncCalendarWithConflictCheck } from '../lib/googleCalendar'
import {
  dismissPushPrompt,
  enablePushNotifications,
  shouldPromptForPushAfterCalendarConnect,
} from '../lib/pushNotifications'
import { CALENDAR_BACKGROUND_SYNC_MS, CALENDAR_NOTICE_DISMISS_MS } from '../lib/calendarSyncConstants'
import { hasProPlusAccess } from '../lib/planTier'
import { loadUserProtocol } from '../lib/loadProtocol'
import { ensureProfile } from '../lib/profile'
import { loadProfileStats, type ProfileStats } from '../lib/profileStats'
import { loadUserTier } from '../lib/tier'
import { confirmCheckoutSession, syncStripeBilling } from '../lib/stripeBilling'
import { ensureProtocolReady } from '../lib/ensureProtocolReady'
import { localeFromProfile } from '../lib/groceryPrices'
import { updateWorkoutScheduleTime } from '../lib/trainingScheduleTime'
import { supabase } from '../lib/supabaseClient'
import Loading from './Loading'

const AppHome = () => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [firstName, setFirstName] = useState('')
  const [userId, setUserId] = useState<string | null>(null)
  const [protocol, setProtocol] = useState<Awaited<ReturnType<typeof loadUserProtocol>>['protocol']>(null)
  const [tier, setTier] = useState<Awaited<ReturnType<typeof loadUserTier>>['tier']>(null)
  const [stats, setStats] = useState<ProfileStats | null>(null)
  const [conflict, setConflict] = useState<ConflictAlert | null>(null)
  const [weeklyOpen, setWeeklyOpen] = useState(false)
  const [calendarSheetOpen, setCalendarSheetOpen] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [savingWorkoutTimeId, setSavingWorkoutTimeId] = useState<string | null>(null)
  const [googleConnecting, setGoogleConnecting] = useState(false)
  const [externalEvents, setExternalEvents] = useState<{ id: string; title: string; starts_at: string; ends_at: string; is_work_event?: boolean }[]>([])
  const [completedScheduleIds, setCompletedScheduleIds] = useState<string[]>([])
  const [batchGrocery, setBatchGrocery] = useState<GroceryItem[] | null>(null)
  const [nearbyStores, setNearbyStores] = useState<NearbyStore[]>([])
  const [preferredCurrency, setPreferredCurrency] = useState('EUR')
  const [countryCode, setCountryCode] = useState('AT')
  const [currencyCode, setCurrencyCode] = useState('EUR')
  const [currencySymbol, setCurrencySymbol] = useState('€')
  const [isVegetarian, setIsVegetarian] = useState(false)
  const [showDisclaimer, setShowDisclaimer] = useState(false)
  const [calendarNotice, setCalendarNotice] = useState('')
  const billingRestoreAttempted = useRef(false)
  const calendarNoticeTimer = useRef<number | null>(null)

  const { language, loading: languageLoading } = useUserLanguage(userId)

  const dietaryLabel = useMemo(
    () => (isVegetarian ? t('app.dietary.vegetarian') : t('app.dietary.omnivore')),
    [isVegetarian, t],
  )

  const cycleProfile = useMemo(
    () =>
      stats
        ? {
            sex: stats.sex,
            last_period_start_date: stats.last_period_start_date,
            avg_cycle_length: stats.avg_cycle_length,
            avg_period_duration: stats.avg_period_duration,
          }
        : null,
    [stats],
  )

  const shoppingItems = batchGrocery ?? protocol?.grocery ?? []

  const tabParam = searchParams.get('tab')
  const activeTab: AppTabId = tabParam === 'meals' || tabParam === 'shopping' || tabParam === 'profile' ? tabParam : 'coach'

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' in window ? ('instant' as ScrollBehavior) : 'auto' })
  }, [activeTab])
  const gate = useFeatureGate(tier)

  const setActiveTab = (tab: AppTabId) => setSearchParams({ tab }, { replace: true })

  const handleDisclaimerAccepted = useCallback(async () => {
    if (!userId) return
    await supabase
      .from('profiles')
      .update({ disclaimer_accepted_at: new Date().toISOString() })
      .eq('id', userId)
    setShowDisclaimer(false)
  }, [userId])

  const reloadExternalEvents = useCallback(async (uid: string, calendarEnabled: boolean) => {
    if (!calendarEnabled) {
      setExternalEvents([])
      return
    }
    const { data: events } = await supabase
      .from('external_calendar_events')
      .select('id, title, starts_at, ends_at, is_work_event')
      .eq('user_id', uid)
      .gte('ends_at', new Date().toISOString())
    if (events) setExternalEvents(events)
  }, [])

  const showCalendarNotice = useCallback((message: string) => {
    setCalendarNotice(message)
    if (calendarNoticeTimer.current) window.clearTimeout(calendarNoticeTimer.current)
    calendarNoticeTimer.current = window.setTimeout(() => {
      setCalendarNotice('')
      calendarNoticeTimer.current = null
    }, CALENDAR_NOTICE_DISMISS_MS)
  }, [])

  const reloadConflictAlert = useCallback(async (uid: string) => {
    const { alert, error: conflictError } = await loadPendingConflictAlert(uid)
    if (conflictError) {
      console.error('Failed to load conflict alert:', conflictError)
      return
    }
    setConflict(alert)
  }, [])

  const refreshAppData = useCallback(async (uid: string) => {
    const [{ protocol: loaded, error: loadError }, { tier: loadedTier }, { stats: loadedStats }] = await Promise.all([
      loadUserProtocol(uid),
      loadUserTier(uid),
      loadProfileStats(uid),
    ])

    if (loadError) setError(loadError)
    else setProtocol(loaded)
    setTier(loadedTier)
    setStats(loadedStats)

    const calendarActive = Boolean(
      loadedTier?.calendar_sync_enabled && hasProPlusAccess(loadedTier.plan ?? 'free'),
    )

    if (calendarActive) {
      const { error: detectInvokeError } = await supabase.functions.invoke('detect-calendar-conflicts', { body: {} })
      if (detectInvokeError) {
        console.error('Conflict detection failed:', detectInvokeError.message)
      }
    }

    await reloadConflictAlert(uid)
    await reloadExternalEvents(uid, calendarActive)

    const { data: completedLogs } = await supabase
      .from('daily_workout_logs')
      .select('schedule_id')
      .eq('user_id', uid)
      .eq('status', 'completed')
    setCompletedScheduleIds((completedLogs ?? []).map((row) => String(row.schedule_id)))
  }, [reloadConflictAlert, reloadExternalEvents])

  const finishCalendarSync = useCallback(async () => {
    if (userId) await refreshAppData(userId)
    setSyncing(false)
    setCalendarSheetOpen(false)
  }, [refreshAppData, userId])

  const runCalendarSyncPipeline = useCallback(async (options?: { background?: boolean }) => {
    if (!userId || !gate.canCalendarSync) {
      if (!options?.background) navigate('/paywall')
      return null
    }

    if (!options?.background) {
      setSyncing(true)
      setError('')
    }

    const result = await syncCalendarWithConflictCheck()
    if (!result.ok) {
      if (!options?.background) {
        setSyncing(false)
        setError(result.error ?? t('app.errors.calendarSync'))
      } else {
        console.warn('Background calendar sync failed:', result.error)
      }
      return null
    }

    await finishCalendarSync()

    const syncedCount = result.synced ?? 0
    const conflictCount = result.conflicts ?? 0
    const notice = conflictCount > 0
      ? t('calendar.syncSuccessWithConflicts', { synced: syncedCount, conflicts: conflictCount })
      : t('calendar.syncSuccess', { synced: syncedCount })

    if (!options?.background) {
      showCalendarNotice(notice)
      if (shouldPromptForPushAfterCalendarConnect()) {
        dismissPushPrompt()
        void enablePushNotifications().catch((error) => {
          console.warn('Push permission prompt skipped:', error)
        })
      }
    } else if (conflictCount > 0) {
      showCalendarNotice(t('calendar.backgroundConflictsFound', { count: conflictCount }))
    } else if (shouldPromptForPushAfterCalendarConnect()) {
      showCalendarNotice(t('calendar.pushPrompt'))
      dismissPushPrompt()
    }

    if (result.error) {
      console.warn('Calendar synced but conflict detection reported:', result.error)
    }

    return result
  }, [finishCalendarSync, gate.canCalendarSync, navigate, showCalendarNotice, t, userId])

  const runGoogleCalendarSync = useCallback(async () => {
    await runCalendarSyncPipeline()
  }, [runCalendarSyncPipeline])

  useEffect(() => {
    if (!userId || !tier || billingRestoreAttempted.current) return
    if (tier.plan !== 'free' || !tier.stripe_customer_id) return

    billingRestoreAttempted.current = true
    void syncStripeBilling()
      .then(() => refreshAppData(userId))
      .catch((error) => {
        console.warn('Auto billing restore skipped:', error)
      })
  }, [refreshAppData, tier, userId])

  useEffect(() => {
    if (!userId || !tier?.calendar_sync_enabled || !gate.canCalendarSync) return

    const intervalId = window.setInterval(() => {
      void runCalendarSyncPipeline({ background: true })
    }, CALENDAR_BACKGROUND_SYNC_MS)

    return () => window.clearInterval(intervalId)
  }, [gate.canCalendarSync, runCalendarSyncPipeline, tier?.calendar_sync_enabled, userId])

  useEffect(() => {
    return () => {
      if (calendarNoticeTimer.current) window.clearTimeout(calendarNoticeTimer.current)
    }
  }, [])

  useEffect(() => {
    let mounted = true

    const init = async () => {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
      if (!mounted) return
      if (sessionError || !sessionData.session?.user) {
        navigate('/')
        return
      }

      const user = sessionData.session.user
      setUserId(user.id)

      const { profile, error: profileError } = await ensureProfile(user)
      if (!mounted) return
      if (profileError || !profile) {
        setError(profileError ?? t('app.errors.profileLoad'))
        setLoading(false)
        return
      }

      if (!profile.onboarding_completed) {
        navigate('/onboarding')
        return
      }

      if (!profile.paywall_dismissed_at && searchParams.get('checkout') !== 'success') {
        navigate('/paywall')
        return
      }

      if (!profile.disclaimer_accepted_at) {
        setShowDisclaimer(true)
      }

      setFirstName(String(profile.first_name ?? ''))
      setNearbyStores((profile.nearby_stores as NearbyStore[]) ?? [])
      setPreferredCurrency(String(profile.preferred_currency ?? 'EUR'))
      const locale = localeFromProfile(profile)
      setCountryCode(locale.countryCode)
      setCurrencyCode(locale.currencyCode)
      setCurrencySymbol(locale.currencySymbol)
      setIsVegetarian(Boolean(profile.vegetarian))

      const { ok: protocolReady, error: protocolError } = await ensureProtocolReady(user.id, profile)
      if (!mounted) return
      if (!protocolReady) {
        setError(protocolError ?? t('app.errors.connectionIssue'))
        setLoading(false)
        return
      }

      await refreshAppData(user.id)
      if (!mounted) return
      setLoading(false)
    }

    init()
    return () => {
      mounted = false
    }
  }, [navigate, refreshAppData, searchParams, t])

  useEffect(() => {
    if (searchParams.get('checkout') !== 'success' || !userId) return

    const sessionId = searchParams.get('session_id')?.trim()
    let cancelled = false
    let poll: number | undefined

    void (async () => {
      if (sessionId) {
        try {
          await confirmCheckoutSession(sessionId)
        } catch (syncError) {
          if (!cancelled) {
            console.error('Checkout confirmation failed:', syncError)
          }
        }
      }

      if (cancelled) return

      let attempts = 0
      poll = window.setInterval(async () => {
        attempts += 1
        await refreshAppData(userId)
        if (attempts >= 8 && poll) window.clearInterval(poll)
      }, 2000)
    })()

    const next = new URLSearchParams(searchParams)
    next.delete('checkout')
    next.delete('session_id')
    setSearchParams(next, { replace: true })

    return () => {
      cancelled = true
      if (poll) window.clearInterval(poll)
    }
  }, [refreshAppData, searchParams, setSearchParams, userId])

  useEffect(() => {
    const calendarStatus = searchParams.get('calendar')
    if (!userId || loading) return

    if (calendarStatus === 'connected') {
      void runGoogleCalendarSync()
      const next = new URLSearchParams(searchParams)
      next.delete('calendar')
      setSearchParams(next, { replace: true })
      return
    }

    if (calendarStatus === 'error') {
      const reason = searchParams.get('reason')
      const friendlyReason = reason
        ? decodeURIComponent(reason)
            .replace(/_/g, ' ')
            .replace('Google Calendar fetch failed (403)', t('app.errors.googleCalendarDenied'))
        : null
      setError(
        friendlyReason ? t('app.errors.googleCalendarPrefix', { reason: friendlyReason }) : t('app.errors.googleConnectFailed'),
      )
      const next = new URLSearchParams(searchParams)
      next.delete('calendar')
      next.delete('reason')
      setSearchParams(next, { replace: true })
    }
  }, [loading, runGoogleCalendarSync, searchParams, setSearchParams, t, userId])

  const handleSignOut = async () => {
    await supabase.auth.signOut()
    window.location.href = '/'
  }

  const handleWellnessDataDeleted = () => {
    navigate('/onboarding')
  }

  const handleGoogleCalendarConnect = async () => {
    if (!gate.canCalendarSync) {
      navigate('/paywall')
      return
    }

    setGoogleConnecting(true)
    setError('')

    const { ok, error: connectError } = await startGoogleCalendarOAuth()
    if (!ok) {
      setGoogleConnecting(false)
      setError(connectError ?? t('app.errors.googleSignInFailed'))
    }
  }

  const handleSyncCalendar = () => {
    if (!gate.canCalendarSync) {
      navigate('/paywall')
      return
    }
    if (tier?.calendar_sync_enabled) {
      void runGoogleCalendarSync()
      return
    }
    setCalendarSheetOpen(true)
  }

  const handleRescheduleConflict = async (slot: SuggestedSlot) => {
    if (!conflict?.kilo_block_id) {
      setError(t('app.errors.rescheduleNotFound'))
      return
    }

    const { error: rescheduleError } = await rescheduleConflict(
      conflict.id,
      conflict.kilo_block_id,
      slot,
      conflict.occurrence_date,
    )
    if (rescheduleError) {
      setError(rescheduleError)
      return
    }

    if (userId) {
      await refreshAppData(userId)
      if (tier?.calendar_sync_enabled) {
        void runGoogleCalendarSync()
      }
    }
  }

  const handleDismissConflict = async () => {
    if (!conflict) return

    const { error: dismissError } = await dismissConflict(conflict.id)
    if (dismissError) {
      setError(dismissError)
      return
    }

    if (userId) await refreshAppData(userId)
  }

  const handleUpdateWorkoutTime = async (scheduleId: string, startTime: string, endTime: string) => {
    setSavingWorkoutTimeId(scheduleId)
    setError('')

    const { data, error: updateError } = await updateWorkoutScheduleTime(scheduleId, startTime, endTime)
    if (updateError) {
      setSavingWorkoutTimeId(null)
      setError(updateError)
      throw new Error(updateError)
    }

    if (userId) {
      await refreshAppData(userId)
    }

    setSavingWorkoutTimeId(null)

    if (data?.calendar_synced) {
      showCalendarNotice(t('calendar.weekly.timeUpdatedSynced'))
    } else {
      showCalendarNotice(t('calendar.weekly.timeUpdated'))
    }
  }

  if (loading || languageLoading) return <Loading />

  return (
    <main className="min-h-screen bg-kilo-page pb-20 text-kilo-text">
      {showDisclaimer ? <FullDisclaimerModal onAccept={() => void handleDisclaimerAccepted()} /> : null}
      <div className="mx-auto max-w-lg px-4 py-4">
        {calendarNotice ? (
          <div className="mb-4 flex items-start gap-2 rounded-2xl border border-kilo-accent/30 bg-kilo-accent/10 p-3 text-sm text-kilo-accent">
            <span className="mt-0.5">✓</span>
            <span>{calendarNotice}</span>
          </div>
        ) : null}

        {error ? <div className="mb-4 rounded-2xl border border-red-900/50 bg-red-950/40 p-3 text-sm text-red-400">{error}</div> : null}

        {activeTab === 'coach' && (
          <CoachTab
            training={protocol?.training ?? []}
            isPremium={gate.isPro}
            canWearables={gate.canWearables}
            canPredictiveCoach={gate.canPredictiveCoach}
            canCalendarSync={gate.canCalendarSync}
            canWeeklyCalendarView={gate.canWeeklyCalendarView}
            userId={userId}
            bodyWeightKg={stats?.current_weight_kg ?? null}
            cycleProfile={cycleProfile}
            calendarSyncEnabled={Boolean(tier?.calendar_sync_enabled)}
            dietaryLabel={dietaryLabel}
            meals={protocol?.meals ?? []}
            grocery={protocol?.grocery ?? []}
            conflict={conflict}
            onOpenWeeklyView={() => setWeeklyOpen(true)}
            onOpenShopping={() => setActiveTab('shopping')}
            onOpenMeals={() => setActiveTab('meals')}
            onWorkoutUpdated={() => userId && refreshAppData(userId)}
            onConnectCalendar={handleSyncCalendar}
            onUpgrade={() => navigate('/paywall')}
            onRescheduleConflict={handleRescheduleConflict}
            onDismissConflict={handleDismissConflict}
          />
        )}
        {activeTab === 'meals' && (
          <MealsTab
            key={language}
            userId={userId}
            meals={protocol?.meals ?? []}
            grocery={protocol?.grocery ?? []}
            cycleProfile={cycleProfile}
            onBatchGroceryChange={setBatchGrocery}
          />
        )}
        {activeTab === 'shopping' && (
          <ShoppingTab
            items={shoppingItems}
            nearbyStores={nearbyStores}
            isPremium={gate.canOptimizedShopping}
            preferredCurrency={preferredCurrency}
            countryCode={countryCode}
            currencyCode={currencyCode}
            currencySymbol={currencySymbol}
            onItemUpdated={() => userId && refreshAppData(userId)}
          />
        )}
        {activeTab === 'profile' && (
          <ProfileTab
            userId={userId}
            firstName={firstName}
            tier={tier}
            stats={stats}
            onSignOut={handleSignOut}
            onUpgrade={() => navigate('/paywall')}
            onSyncCalendar={handleSyncCalendar}
            calendarSyncing={syncing}
            onMetricsUpdated={() => userId && refreshAppData(userId)}
            onBillingUpdated={() => userId && refreshAppData(userId)}
            onWellnessDataDeleted={handleWellnessDataDeleted}
          />
        )}
      </div>

      <BottomTabBar active={activeTab} onChange={setActiveTab} />

      {gate.canWeeklyCalendarView ? (
        <WeeklyCalendarSheet
          open={weeklyOpen}
          onClose={() => setWeeklyOpen(false)}
          training={protocol?.training ?? []}
          externalEvents={externalEvents}
          completedScheduleIds={completedScheduleIds}
          onSyncCalendar={handleSyncCalendar}
          calendarConnected={Boolean(tier?.calendar_sync_enabled)}
          onUpdateWorkoutTime={handleUpdateWorkoutTime}
          savingScheduleId={savingWorkoutTimeId}
        />
      ) : null}

      <CalendarConnectSheet
        open={calendarSheetOpen}
        onClose={() => setCalendarSheetOpen(false)}
        onGoogleConnect={() => void handleGoogleCalendarConnect()}
        onGoogleSync={() => void runGoogleCalendarSync()}
        syncing={syncing}
        googleConnecting={googleConnecting}
        googleConnected={Boolean(tier?.calendar_sync_enabled)}
      />
    </main>
  )
}

export default AppHome
